/* eslint-disable @typescript-eslint/no-explicit-any */
import { CollectionDataConfigsRoute } from 'routes/logged-in/CollectionDataConfigsRoute';
import { ConsumerPageWidgetsRoute } from 'routes/logged-in/ConsumerPageWidgetsRoute';
import { Redirect, Route, Switch } from 'react-router-dom';
import { StoreType, useActions } from 'utils/connect';
import { asyncComponent } from '@tock/shared/utils/asyncComponent';
import { core } from 'types';
import { hasEngRoleSelector, hasMinRoleFinanceSelector } from 'selectors/rolesSelectors';
import { toJSON } from '@tock/coretypes';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useMountEffect } from '@tock/hooks';
import { useSelector } from 'react-redux';
import ClowncarMenu, { ClowncarMenuSections } from 'components/menu/ClowncarMenu';
import NotFoundRedirect from 'routes/NotFoundRedirect';
import NotificationsRoute from 'components/notifications/NotificationsRoute';
import TockApiKeyRoute from './logged-in/TockApiKeyRoute';
import _ from 'lodash';

const AccoladeTypeRoute = asyncComponent(() => import('routes/logged-in/AccoladeTypeRoute'));
const AchRoute = asyncComponent(() => import('routes/logged-in/AchRoute'));
const AdminEmailsRoute = asyncComponent(() => import('routes/logged-in/AdminEmailsRoute'));
const BusinessesRequiringVerificationRoute = asyncComponent(
  () => import('routes/logged-in/BusinessesRequiringVerificationRoute')
);
const BusinessesRoute = asyncComponent(() => import('routes/logged-in/BusinessesRoute'));
const SalesforceBusinessRoute = asyncComponent(
  () => import('routes/logged-in/SalesforceBusinessRoute')
);
const BusinessGroupsRoute = asyncComponent(() => import('routes/logged-in/BusinessGroupsRoute'));
const BusinessesPhotosRoute = asyncComponent(
  () => import('routes/logged-in/BusinessesPhotosRoute')
);
const BusinessPhotosRoute = asyncComponent(() => import('routes/logged-in/BusinessPhotosRoute'));

const BusinessTaxMigrationRoute = asyncComponent(
  () => import('routes/logged-in/BusinessTaxMigrationRoute')
);
const BusinessRoute = asyncComponent(() => import('routes/logged-in/BusinessRoute'));
const BusinessGroupRoute = asyncComponent(() => import('routes/logged-in/BusinessGroupRoute'));
const BusinessSearchAttributesEditRoute = asyncComponent(
  () => import('routes/logged-in/BusinessSearchAttributesEditRoute')
);
const BusinessSearchAttributesRoute = asyncComponent(
  () => import('routes/logged-in/BusinessSearchAttributesRoute')
);
const BusinessVerificationRoute = asyncComponent(
  () => import('routes/logged-in/BusinessVerificationRoute')
);
const ConsumerPageRoute = asyncComponent(() => import('routes/logged-in/ConsumerPageRoute'));
const ConsumerPagesRoute = asyncComponent(() => import('routes/logged-in/ConsumerPagesRoute'));
const EditMetroAreaRoute = asyncComponent(() => import('routes/logged-in/EditMetroAreaRoute'));
const FeatureHistoryRoute = asyncComponent(() => import('routes/logged-in/FeatureHistoryRoute'));
const FeaturesRoute = asyncComponent(() => import('routes/logged-in/FeaturesRoute'));
const ExperimentsRoute = asyncComponent(() => import('routes/logged-in/ExperimentsRoute'));
const GiftsRoute = asyncComponent(() => import('routes/logged-in/GiftsRoute'));
const GuestProfileTagsRoute = asyncComponent(
  () => import('routes/logged-in/GuestProfileTagsRoute')
);
const ImporterRoute = asyncComponent(() => import('routes/logged-in/imports/ImporterRoute'));
const ImportStatusRoute = asyncComponent(
  () => import('routes/logged-in/imports/ImportStatusRoute')
);
const ImportUndoStatusRoute = asyncComponent(
  () => import('routes/logged-in/imports/ImportUndoStatusRoute')
);
const ImportUndoRoute = asyncComponent(() => import('routes/logged-in/imports/ImportUndoRoute'));
const MenuMigratorRoute = asyncComponent(() => import('routes/logged-in/MenuMigratorRoute'));
const DashboardAccessRoute = asyncComponent(() => import('routes/logged-in/DashboardAccessRoute'));
const DashboardGrantAccessRoute = asyncComponent(
  () => import('routes/logged-in/DashboardGrantAccessRoute')
);
const MetroAreasRoute = asyncComponent(() => import('routes/logged-in/MetroAreasRoute'));
const NamedConfigurationsRoute = asyncComponent(
  () => import('routes/logged-in/NamedConfigurationsRoute')
);
const HelpMenuRoute = asyncComponent(() => import('routes/logged-in/HelpMenuRoute'));
const HelpMenuFormRoute = asyncComponent(() => import('routes/logged-in/HelpMenuFormRoute'));
const OnboardingRoute = asyncComponent(() => import('routes/logged-in/OnboardingRoute'));
const PartnersRoute = asyncComponent(() => import('routes/logged-in/PartnersRoute'));
const PhoneNumberAllocationEditRoute = asyncComponent(
  () => import('routes/logged-in/PhoneNumberAllocationEditRoute')
);
const PhoneNumberPoolRoute = asyncComponent(() => import('routes/logged-in/PhoneNumberPoolRoute'));
const RebalancerRoute = asyncComponent(() => import('routes/logged-in/RebalancerRoute'));
const ScheduleHistoryRoute = asyncComponent(() => import('routes/logged-in/ScheduleHistory'));

const ServiceEventsRoute = asyncComponent(() => import('routes/logged-in/ServiceEventsRoute'));
const ServiceEventsDetailsRoute = asyncComponent(
  () => import('./logged-in/ServiceEventsDetailsRoute')
);
const SubscriptionsRoute = asyncComponent(() => import('routes/logged-in/SubscriptionsRoute'));
const UsersRoute = asyncComponent(() => import('routes/logged-in/UsersRoute'));
const FinancePagesRoute = asyncComponent(
  () => import('routes/logged-in/finance/FinancePagesRoute')
);
const TaxPagesRoute = asyncComponent(() => import('routes/logged-in/taxes/TaxPagesRoute'));
const TagAdminRoute = asyncComponent(() => import('routes/logged-in/TagAdminRoute'));
const TockTenRoute = asyncComponent(() => import('routes/logged-in/TockTenRoute'));

const SubscriptionsControlCenter = asyncComponent(
  () => import('routes/logged-in/subscriptions-control-center/SubscriptionsControlCenterRoute')
);

const EntitlementSetsRoute = asyncComponent(
  () => import('routes/logged-in/subscriptions-control-center/EntitlementSetsRoute')
);
const SubscriptionPlansRouteCanary = asyncComponent(
  () => import('routes/logged-in/subscriptions-control-center/SubscriptionPlansRouteCanary')
);

const SubscriptionAddonsRoute = asyncComponent(
  () => import('routes/logged-in/subscriptions-control-center/SubscriptionAddonsRoute')
);

const CreateSubscriptionAddonRoute = asyncComponent(
  () => import('routes/logged-in/subscriptions-control-center/CreateSubscriptionAddonRoute')
);

const EditSubscriptionAddonRoute = asyncComponent(
  () => import('routes/logged-in/subscriptions-control-center/EditSubscriptionAddonRoute')
);

const CreateEntitlementSetRoute = asyncComponent(
  () => import('routes/logged-in/subscriptions-control-center/CreateEntitlementSetRoute')
);

const EditEntitlementSetRoute = asyncComponent(
  () => import('routes/logged-in/subscriptions-control-center/EditEntitlementSetRoute')
);
const CreateSubscriptionRoute = asyncComponent(
  () => import('routes/logged-in/subscriptions-control-center/CreatePaymentSubscriptionPlanRoute')
);
const EditPaymentSubscriptionRoute = asyncComponent(
  () => import('routes/logged-in/subscriptions-control-center/EditPaymentSubscriptionRoute')
);

interface ConnectProps {
  isEng: boolean;
  isMinRoleFinance: boolean;
}

const TockLoggedInRoute: React.FunctionComponent<React.PropsWithChildren<{}>> = () => {
  const { actions } = useActions();
  const isHelpMenuClowncarEnabled = useFeatureFlag('HELP_MENU_CLOWNCAR_ADMIN_INTERFACE');
  const isTockTenConfigEnabled = useFeatureFlag('TOCK_TEN_CONFIG');

  const { isEng, isMinRoleFinance } = useSelector((state: StoreType): ConnectProps => {
    return {
      isEng: hasEngRoleSelector(state as any),
      isMinRoleFinance: hasMinRoleFinanceSelector(state as any),
    };
  });

  useMountEffect(() => {
    actions.app.fetchLoggedInProfile();
    actions.app.updateAuthenticationRoles().then((response: ProtoResponse<any>) => {
      const roles = toJSON<core.RoleResponse>(response.payload);
      if (
        !_.includes(roles.role, 'ENG') &&
        !_.includes(roles.role, 'HOSPITALITY') &&
        !_.includes(roles.role, 'FINANCE')
      ) {
        actions.app.logout();
        actions.toast.showError("Oops! You don't have access to Tock's dashboard.");
      }
    });
    actions.features.fetchGlobalFeatures();
  });

  const sections = (): ClowncarMenuSections => {
    return {
      home: [
        {
          text: 'Select app',
          to: '/',
          hide: !isEng,
        },
      ],
      sales: [
        {
          exact: false,
          text: 'Onboarding',
          to: '/tock/onboarding',
        },
      ],
      am: [
        {
          text: 'Businesses',
          to: '/tock/businesses',
        },
        {
          text: 'Business Groups',
          to: '/tock/business-groups',
        },
        {
          text: 'Business Verification',
          to: '/tock/business-verification',
        },
        {
          text: 'Subscriptions',
          to: '/tock/subscriptions',
        },
        {
          text: 'Importer',
          to: '/tock/importer',
        },
        { text: 'Menu Migrator', to: '/tock/menu-migrator' },
        { text: 'Temporary dashboard access', to: '/tock/dashboard-access' },
        { text: 'Tock api key management', to: '/tock/tock-api-key' },
      ],

      finance: [
        { text: 'Finance', to: '/tock/finance', hide: !isMinRoleFinance },
        { text: 'Finance - Global Tools', to: '/tock/finance/global', hide: !isMinRoleFinance },
        { text: 'Sales Tax', to: '/tock/taxes' },
      ],
      tockPages: [
        {
          text: 'Subscription Control Center',
          to: '/tock/subscriptions-control-center',
        },
        {
          text: 'Consumer Pages',
          to: '/tock/consumer-pages',
        },
        {
          text: 'Widgets',
          to: '/tock/consumer-page-widgets',
        },
        {
          text: 'Data Configurations',
          to: '/tock/collection-data-configs',
        },
        {
          text: 'Named Configurations',
          to: '/tock/named-configurations',
        },
        {
          text: 'Help Menu',
          to: '/tock/help-menu',
          hide: !isHelpMenuClowncarEnabled,
        },
      ],

      marketing: [
        {
          text: 'Tock 10',
          to: '/tock/tock-ten',
          hide: !isTockTenConfigEnabled,
        },
        {
          text: 'Business Attributes',
          to: '/tock/business-attributes',
        },
        {
          text: 'Search Metro Areas',
          to: '/tock/metro-areas',
        },
        {
          text: 'Tag Admin',
          to: '/tock/tag-admin',
        },

        {
          text: 'Business Photos',
          to: '/tock/business-photos',
        },
      ],

      hospitality: [
        {
          text: 'Features',
          to: '/tock/features',
        },
        {
          text: 'Experiments',
          to: '/tock/experiments',
        },
        {
          text: 'Users',
          to: '/tock/users',
        },
        {
          text: 'Admin Emails',
          to: '/tock/adminemails',
        },
        {
          text: 'Phone Numbers',
          to: '/tock/phone-numbers',
        },
        {
          text: 'Bank ACH Transfer',
          to: '/tock/ach',
        },
        { text: 'Award Types', to: '/tock/accolade-types' },
        { text: 'Gifts', to: '/tock/gifts' },
        { text: 'Guest Profile Tags', to: '/tock/guest-profile-tags' },
        { text: 'Business Tax Migration', to: '/tock/business-tax-migration' },
      ],

      eng: [
        { text: 'Schedule History', to: '/tock/schedule-history' },
        { text: 'Rebalancer Tools', to: '/tock/rebalancer' },
        {
          text: 'Service Events',
          to: '/tock/events',
        },
        {
          text: 'Partners',
          to: '/tock/partners',
        },
        {
          text: 'Notifications',
          to: '/tock/notifications',
        },
      ],

      last: [
        {
          text: 'Logout',
          to: '/logout',
        },
      ],
    };
  };

  const defaultRoute = isEng ? '/tock/features' : '/tock/businesses';
  return (
    <div className="Clowncar">
      <ClowncarMenu sections={sections()} />
      <Switch>
        {/* Bare /tock path — silently redirect to default without a "not found" toast */}
        <Route exact={true} path="/tock">
          <Redirect to={defaultRoute} />
        </Route>
        <Route component={AccoladeTypeRoute} path="/tock/accolade-types" />
        <Route component={AchRoute} path="/tock/ach" />
        <Route component={AdminEmailsRoute} path="/tock/adminemails" />
        <Route component={BusinessesRoute} exact={true} path="/tock/businesses" />
        <Route component={BusinessGroupsRoute} exact={true} path="/tock/business-groups" />
        <Route
          component={BusinessSearchAttributesRoute}
          exact={true}
          path="/tock/business-attributes"
        />
        <Route
          component={BusinessSearchAttributesEditRoute}
          path="/tock/business-attributes/:businessId"
        />
        <Route
          component={BusinessesRequiringVerificationRoute}
          exact={true}
          path="/tock/business-verification"
        />
        <Route
          component={BusinessVerificationRoute}
          path="/tock/business-verification/:businessId"
        />
        <Route component={BusinessRoute} path="/tock/businesses/:businessId" />
        <Route component={SalesforceBusinessRoute} path="/tock/sf/businesses/:businessId" />
        <Route component={SalesforceBusinessRoute} path="/tock/sf/businesses" />
        <Route component={BusinessGroupRoute} path="/tock/business-groups/:businessGroupId" />
        <Route component={ServiceEventsRoute} path="/tock/events" />
        <Route component={ServiceEventsDetailsRoute} path="/tock/events-details/:businessId" />
        <Route component={FeatureHistoryRoute} path="/tock/features/:id/history" />
        <Route component={FeaturesRoute} path="/tock/features" />
        <Route component={ExperimentsRoute} path="/tock/experiments" />

        <Route exact path="/tock/subscriptions-control-center">
          <Redirect to="/tock/subscriptions-control-center/entitlement-sets" />
        </Route>
        <Route path="/tock/subscriptions-control-center">
          <SubscriptionsControlCenter>
            <Route
              path="/tock/subscriptions-control-center/entitlement-sets/new"
              component={CreateEntitlementSetRoute}
            />
            <Route
              path="/tock/subscriptions-control-center/entitlement-sets/:id/edit"
              component={EditEntitlementSetRoute}
            />
            <Route
              path="/tock/subscriptions-control-center/entitlement-sets"
              component={EntitlementSetsRoute}
            />
            <Route
              path="/tock/subscriptions-control-center/subscriptions/new"
              component={CreateSubscriptionRoute}
            />
            <Route
              path="/tock/subscriptions-control-center/subscriptions/:id/edit"
              component={EditPaymentSubscriptionRoute}
            />
            <Route
              path="/tock/subscriptions-control-center/subscriptions"
              component={SubscriptionPlansRouteCanary}
            />
            <Route
              path="/tock/subscriptions-control-center/add-ons/new"
              component={CreateSubscriptionAddonRoute}
            />
            <Route
              path="/tock/subscriptions-control-center/add-ons/:id/edit"
              component={EditSubscriptionAddonRoute}
            />
            <Route
              path="/tock/subscriptions-control-center/add-ons"
              component={SubscriptionAddonsRoute}
            />
          </SubscriptionsControlCenter>
        </Route>
        <Route component={HelpMenuRoute} path="/tock/help-menu" />
        <Route component={HelpMenuFormRoute} path="/tock/help-menu-create" />
        <Route component={HelpMenuFormRoute} path="/tock/help-menu-edit/:id" />

        <Route component={GiftsRoute} path="/tock/gifts" />
        <Route component={GuestProfileTagsRoute} path="/tock/guest-profile-tags" />
        <Route
          component={ImportUndoStatusRoute}
          path="/tock/importer/undo-import/:businessId/status"
        />
        <Route
          component={ImportUndoRoute}
          path="/tock/importer/undo-import/:businessId/:importUuid"
        />
        <Route component={ImportStatusRoute} path="/tock/importer/:businessId" />
        <Route component={ImporterRoute} path="/tock/importer" />
        <Route component={MenuMigratorRoute} path="/tock/menu-migrator" />
        <Route component={DashboardGrantAccessRoute} path="/tock/dashboard-access/:requestId" />
        <Route component={DashboardAccessRoute} path="/tock/dashboard-access" />
        <Route component={OnboardingRoute} path="/tock/onboarding" />
        <Route component={PartnersRoute} path="/tock/partners" />
        <Route component={NotificationsRoute} path="/tock/notifications" />
        <Route
          component={PhoneNumberAllocationEditRoute}
          path="/tock/phone-numbers/:phoneNumberId"
        />
        <Route component={PhoneNumberPoolRoute} path="/tock/phone-numbers" />
        <Route component={RebalancerRoute} path="/tock/rebalancer" />
        <Route component={ScheduleHistoryRoute} path="/tock/schedule-history" />
        <Route component={SubscriptionsRoute} path="/tock/subscriptions" />
        <Route component={UsersRoute} path="/tock/users" />
        <Route component={EditMetroAreaRoute} path="/tock/metro-areas/:metroAreaSlug" />
        <Route component={MetroAreasRoute} path="/tock/metro-areas" />
        <Route
          component={ConsumerPageRoute}
          path="/tock/consumer-pages/:consumerPageConfigPathId"
        />
        <Route component={ConsumerPagesRoute} path="/tock/consumer-pages" />
        <Route component={FinancePagesRoute} path="/tock/finance" />
        <Route component={TaxPagesRoute} path="/tock/taxes" />
        <Route component={NamedConfigurationsRoute} path="/tock/named-configurations" />
        <Route component={ConsumerPageWidgetsRoute} path="/tock/consumer-page-widgets" />
        <Route component={CollectionDataConfigsRoute} path="/tock/collection-data-configs" />
        <Route component={BusinessPhotosRoute} path="/tock/business-photos/:domainName" />
        <Route component={BusinessesPhotosRoute} exact={true} path="/tock/business-photos" />
        <Route component={TockApiKeyRoute} path="/tock/tock-api-key" />
        <Route component={TockTenRoute} exact={true} path="/tock/tock-ten" />

        <Route component={TagAdminRoute} path="/tock/tag-admin" />
        <Route component={BusinessTaxMigrationRoute} path="/tock/business-tax-migration" />
        <Route>
          <NotFoundRedirect to={defaultRoute} />
        </Route>
      </Switch>
    </div>
  );
};

export default TockLoggedInRoute;
